import { Plane, Heart, Briefcase, Clock, Shield, Sun } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";

const JET_IMG = "/images/bmw-xm-jet-front.png";
const ESCALADE_IMG = "/images/escalade-wedding-daylight.png";
const COASTAL_IMG = "/images/range-rover-sport-black-v2.png";

function Card({ children, className = "", testId }) {
  return (
    <div
      data-testid={testId}
      className={`group relative overflow-hidden border border-[#E5DEC9] bg-white shadow-sm hover:shadow-lg hover:border-[#C5A059]/70 transition-all duration-500 ${className}`}
    >
      {children}
    </div>
  );
}

export function Services() {
  return (
    <section id="services" className="relative py-28 lg:py-36 bg-[#FAF7F2]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Overline className="text-amber-700">Signature Services</Overline>
          <h2 className="font-serif-lux font-light text-4xl sm:text-5xl lg:text-6xl text-[#111318] mt-4 max-w-2xl leading-tight">
            Every journey, <span className="gold-text italic">composed.</span>
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-4">
          <Reveal className="md:col-span-7" delay={0.1}>
            <Card testId="service-airport-card" className="h-full min-h-[420px]">
              <img
                src={JET_IMG}
                alt="BMW XM awaiting a private jet arrival"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s] ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#16181D]/85 via-[#16181D]/15 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-8 lg:p-10">
                <Plane className="text-amber-400 mb-4" size={26} strokeWidth={1.25} />
                <h3 className="font-serif-lux text-3xl text-amber-50">Airport &amp; Private Jet Transfers</h3>
                <p className="mt-3 text-sm text-zinc-200 max-w-md leading-relaxed">
                  Flight-tracked arrivals at MIA, PBI and every South Florida FBO. Your BMW XM waits planeside
                  where permitted — never a queue, never a delay on our account.
                </p>
              </div>
            </Card>
          </Reveal>

          <Reveal className="md:col-span-5" delay={0.2}>
            <Card testId="service-wedding-card" className="h-full min-h-[420px]">
              <img
                src={ESCALADE_IMG}
                alt="Cadillac Escalade wedding chauffeur service"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s] ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#16181D]/85 via-[#16181D]/15 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-8 lg:p-10">
                <Heart className="text-amber-400 mb-4" size={26} strokeWidth={1.25} />
                <h3 className="font-serif-lux text-3xl text-amber-50">Weddings &amp; Occasions</h3>
                <p className="mt-3 text-sm text-zinc-200 leading-relaxed">
                  Ribboned Escalade, immaculate timing and a chauffeur who understands the weight of the day.
                </p>
              </div>
            </Card>
          </Reveal>

          <Reveal className="md:col-span-4" delay={0.1}>
            <Card testId="service-corporate-card" className="h-full min-h-[280px] p-8 flex flex-col justify-between">
              <Briefcase className="text-amber-600" size={26} strokeWidth={1.25} />
              <div>
                <h3 className="font-serif-lux text-2xl text-[#111318] mt-8">Corporate Roadshows</h3>
                <p className="mt-3 text-sm text-[#525B6C] leading-relaxed">
                  Multi-stop itineraries from Brickell to Palm Beach executed to the minute, with onboard Wi-Fi
                  and absolute discretion between meetings.
                </p>
              </div>
            </Card>
          </Reveal>

          <Reveal className="md:col-span-4" delay={0.2}>
            <Card testId="service-coastal-card" className="h-full min-h-[280px]">
              <img
                src={COASTAL_IMG}
                alt="Range Rover Sport coastal transfer"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s] ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#16181D]/85 via-[#16181D]/10 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-8">
                <Sun className="text-amber-400 mb-4" size={26} strokeWidth={1.25} />
                <h3 className="font-serif-lux text-2xl text-amber-50">Coastal &amp; Worth Avenue</h3>
                <p className="mt-3 text-sm text-zinc-200 leading-relaxed">
                  Sunlit transfers along Worth Avenue, Miami Beach dining and the coastal estates — arrival as
                  polished as the destination.
                </p>
              </div>
            </Card>
          </Reveal>

          <Reveal className="md:col-span-4" delay={0.3}>
            <Card testId="service-assurance-card" className="h-full min-h-[280px] p-8 flex flex-col justify-between">
              <Shield className="text-amber-600" size={26} strokeWidth={1.25} />
              <div>
                <div className="flex items-baseline gap-2 mt-8">
                  <span className="font-serif-lux text-5xl gold-text">24/7</span>
                  <Clock size={16} className="text-[#9AA1AF]" />
                </div>
                <h3 className="font-serif-lux text-2xl text-[#111318] mt-2">Concierge Assurance</h3>
                <p className="mt-3 text-sm text-[#525B6C] leading-relaxed">
                  A direct line to our concierge around the clock — amendments, preferences and last-minute
                  requests handled quietly.
                </p>
              </div>
            </Card>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
